// The fake api's HQ Players and audit log routes (spec/14 §10, §15; spec/07 §3.5, §11.2), in the
// contract's shapes and with its rules — for the component tests and the accessibility test, through
// `createFakeServer` (fake-server.ts), which puts the HQ guard (a platform role, a second factor) in
// front of them. Each route checks its own permission, as the server's permission guard does.
//
//   GET  /v1/hq/players?q=               hq:players_read   a phone, an email or a player id whole; else part of a name
//   GET  /v1/hq/players/{p}              hq:players_read   the platform record
//   POST /v1/hq/players/{p}/ban|unban    hq:players        with a reason
//   GET  /v1/hq/audit                    audit:read        a reason required; every read recorded
//   POST /v1/hq/audit/export             audit:read        CSV, the filtered set exactly; 422 past 10,000 rows
//
// A read of the audit log records what the server records (spec/07 §11.2): one platform-level row
// and one `audit.log.read` row in the log of every organisation whose rows came back, with the
// reader and the reason. They are kept in `state.recorded`, apart from the rows the log lists, so
// what a test lists does not move under it.

import type { AuditEntry, HqPlayer, HqPlayerSummary, PlatformBan, Problem } from '@clavian/api-client';
import { AUDIT_EXPORT_COLUMNS, AUDIT_EXPORT_MAX_ROWS } from '@clavian/contracts';

import type { FakeResponse } from './fake-server';

export const OTHER_ORGANIZATION_ID = 'org_01k5znw4hmfay8vvr14d2pf2dz';
const VENUE_ID = 'ven_01k5znw4hmfay8vvr14d2pf2dc';
const ORGANIZATION_ID = 'org_01k5znw4hmfay8vvr14d2pf2db';

/** A request that wrote two rows, both in Playground Gaming's log: what a support call quotes. */
export const TRACED_REQUEST = 'req_01k5znw4hmfay8vvr14d2pf2t1';
const OTHER_REQUEST = 'req_01k5znw4hmfay8vvr14d2pf2t2';

const device = (deviceKey: string, firstSeenAt: string, lastSeenAt: string) => ({ deviceKey, firstSeenAt, lastSeenAt });

/** Three people on the Platform: two share a first name, one is banned. */
export const HQ_PLAYERS: Readonly<Record<'aarav' | 'diya' | 'aaravShah', HqPlayer>> = {
  aarav: {
    id: 'ply_01k5znw4hmfay8vvr14d2pf2a1',
    username: 'aarav',
    displayName: 'Aarav M',
    firstName: 'Aarav',
    lastName: 'Mehta',
    phoneNumber: '+919876543210',
    email: 'aarav.mehta@example.com',
    contactMasked: false,
    banned: false,
    createdAt: '2026-01-05T10:00:00.000Z',
    phoneNumberVerified: true,
    emailVerified: false,
    signInMethods: ['phone', 'google'],
    twoFactorEnabled: false,
    devices: [
      device('chrome/android/mobile', '2026-01-05T10:00:00.000Z', '2026-09-21T18:30:00.000Z'),
      device('edge/windows/desktop', '2026-03-02T12:00:00.000Z', '2026-09-01T20:00:00.000Z'),
    ],
    platformBan: { playerId: 'ply_01k5znw4hmfay8vvr14d2pf2a1', banned: false, reason: null, expiresAt: null },
  },
  diya: {
    id: 'ply_01k5znw4hmfay8vvr14d2pf2a2',
    username: 'diyarao',
    displayName: null,
    firstName: 'Diya',
    lastName: 'Rao',
    phoneNumber: '+919812345678',
    email: null,
    contactMasked: false,
    banned: true,
    createdAt: '2026-02-11T09:00:00.000Z',
    phoneNumberVerified: true,
    emailVerified: false,
    signInMethods: ['phone', 'password'],
    twoFactorEnabled: true,
    devices: [],
    platformBan: {
      playerId: 'ply_01k5znw4hmfay8vvr14d2pf2a2',
      banned: true,
      reason: 'Chargebacks at three venues in one week',
      expiresAt: null,
    },
  },
  aaravShah: {
    id: 'ply_01k5znw4hmfay8vvr14d2pf2a3',
    username: null,
    displayName: null,
    firstName: 'Aarav',
    lastName: 'Shah',
    phoneNumber: null,
    email: 'aarav.shah@example.com',
    contactMasked: false,
    banned: false,
    createdAt: '2026-04-20T15:00:00.000Z',
    phoneNumberVerified: false,
    emailVerified: true,
    signInMethods: ['email'],
    twoFactorEnabled: false,
    devices: [device('safari/ios/mobile', '2026-04-20T15:00:00.000Z', '2026-04-20T15:00:00.000Z')],
    platformBan: { playerId: 'ply_01k5znw4hmfay8vvr14d2pf2a3', banned: false, reason: null, expiresAt: null },
  },
};

const row = (entry: Omit<AuditEntry, 'recordedAt' | 'actorIp'> & { recordedAt?: string }): AuditEntry => ({
  actorIp: null,
  ...entry,
  recordedAt: entry.recordedAt ?? entry.occurredAt,
});

/** The log, newest first: two organisations' rows and the platform's own. */
export const AUDIT_ROWS: readonly AuditEntry[] = [
  row({
    id: 'aud_01k5znw4hmfay8vvr14d2pf2r5',
    occurredAt: '2026-09-22T08:30:00.000Z',
    actor: { kind: 'member', id: '8a6f0e1c-0000-4000-8000-000000000010', label: 'Kabir Singh' },
    organizationId: ORGANIZATION_ID,
    venueId: VENUE_ID,
    action: 'sessions.comp',
    targetType: 'session',
    targetId: 'ses_01k5znw4hmfay8vvr14d2pf2s1',
    before: { minutes: 0 },
    after: { minutes: 15 },
    reason: 'Station froze mid-match',
    requestId: TRACED_REQUEST,
  }),
  row({
    id: 'aud_01k5znw4hmfay8vvr14d2pf2r4',
    occurredAt: '2026-09-22T08:30:00.000Z',
    actor: { kind: 'member', id: '8a6f0e1c-0000-4000-8000-000000000010', label: 'Kabir Singh' },
    organizationId: ORGANIZATION_ID,
    venueId: VENUE_ID,
    action: 'org.setting.update',
    targetType: 'setting_value',
    targetId: null,
    before: { key: 'sessions.graceMinutes', value: 5 },
    after: { key: 'sessions.graceMinutes', value: 10 },
    reason: null,
    requestId: TRACED_REQUEST,
  }),
  row({
    id: 'aud_01k5znw4hmfay8vvr14d2pf2r3',
    occurredAt: '2026-09-21T19:00:00.000Z',
    actor: { kind: 'member', id: '8a6f0e1c-0000-4000-8000-000000000011', label: 'Meera Iyer' },
    onBehalfOf: { kind: 'support_grant', id: 'grt_01k5znw4hmfay8vvr14d2pf2g1' },
    organizationId: OTHER_ORGANIZATION_ID,
    venueId: null,
    action: 'players.ban',
    targetType: 'player',
    targetId: 'ply_01k5znw4hmfay8vvr14d2pf2a2',
    before: null,
    after: { scope: 'organization' },
    reason: 'Abusive to staff',
    requestId: OTHER_REQUEST,
  }),
  row({
    id: 'aud_01k5znw4hmfay8vvr14d2pf2r2',
    occurredAt: '2026-09-20T06:00:00.000Z',
    actor: { kind: 'system', id: null, label: 'platform:role' },
    organizationId: null,
    venueId: null,
    action: 'identity.user.platform_role.grant',
    targetType: 'user',
    targetId: '8a6f0e1c-0000-4000-8000-000000000002',
    before: { role: null },
    after: { role: 'hq_owner' },
    reason: 'The first HQ owner',
    requestId: null,
  }),
];

const ACTION_FILTER = /^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)*(?:\.\*)?$/;
const FILTER_KEYS = ['actorId', 'actorKind', 'action', 'targetType', 'targetId', 'venueId', 'onBehalfOf', 'requestId', 'from', 'to', 'organizationId'] as const;

export interface RecordedAuditRow {
  readonly organizationId: string | null;
  readonly action: string;
  readonly reason: string;
  readonly actorId: string;
  readonly filter: Readonly<Record<string, string>>;
}

interface Respond {
  readonly json: (status: number, body: unknown, headers?: Record<string, string>) => FakeResponse;
  readonly problem: (status: number, code: string, title: string, extra?: Partial<Problem>) => FakeResponse;
  readonly requestId: string;
}

const summaryOf = (player: HqPlayer): HqPlayerSummary => ({
  id: player.id,
  username: player.username,
  displayName: player.displayName,
  firstName: player.firstName,
  lastName: player.lastName,
  phoneNumber: player.phoneNumber,
  email: player.email,
  contactMasked: player.contactMasked,
  banned: player.banned,
  createdAt: player.createdAt,
});

export function createHqPlayersAudit({ json, problem, requestId }: Respond) {
  const state = {
    players: new Map<string, HqPlayer>(Object.values(HQ_PLAYERS).map((player) => [player.id, structuredClone(player)])),
    audit: [...AUDIT_ROWS] as AuditEntry[],
    /** What the reads and exports of the log recorded, as the server writes it. */
    recorded: [] as RecordedAuditRow[],
  };

  const invalid = (field: string, message: string) =>
    problem(400, 'validation_failed', 'Invalid', { errors: [{ field, code: 'invalid', message }] });

  // -- players ---------------------------------------------------------------------------------

  function search(searchParams: URLSearchParams): FakeResponse {
    const q = (searchParams.get('q') ?? '').trim();
    if (q.length < 2 || q.length > 100) return invalid('q', 'Too small: expected at least 2 characters.');
    const players = [...state.players.values()].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    const lower = q.toLowerCase();
    const found = q.startsWith('+')
      ? players.filter((player) => player.phoneNumber === q)
      : q.includes('@')
        ? players.filter((player) => player.email?.toLowerCase() === lower)
        : q.startsWith('ply_')
          ? players.filter((player) => player.id === lower)
          : players.filter((player) =>
              [player.firstName, player.lastName, player.displayName, player.username].some((part) => part?.toLowerCase().includes(lower)),
            );
    return page(found.map(summaryOf), searchParams);
  }

  function page<T>(items: readonly T[], searchParams: URLSearchParams): FakeResponse {
    const limit = Number(searchParams.get('limit') ?? 25);
    const start = Number(searchParams.get('cursor') ?? 0);
    const data = items.slice(start, start + limit);
    const hasMore = start + limit < items.length;
    return json(200, { data, page: { cursor: hasMore ? String(start + limit) : null, hasMore, count: data.length } });
  }

  function ban(player: HqPlayer, input: Record<string, unknown>): FakeResponse {
    const reason = typeof input['reason'] === 'string' ? input['reason'].trim() : '';
    if (reason === '' || reason.length > 500) return invalid('reason', 'Too small: expected at least 1 character.');
    const expiresAt = typeof input['expiresAt'] === 'string' ? input['expiresAt'] : null;
    if (expiresAt !== null && Date.parse(expiresAt) <= Date.now()) return invalid('expiresAt', 'must be after now');
    const platformBan: PlatformBan = { playerId: player.id, banned: true, reason, expiresAt };
    state.players.set(player.id, { ...player, banned: true, platformBan });
    return json(200, platformBan);
  }

  function unban(player: HqPlayer, input: Record<string, unknown>): FakeResponse {
    const reason = typeof input['reason'] === 'string' ? input['reason'].trim() : '';
    if (reason === '') return invalid('reason', 'Too small: expected at least 1 character.');
    if (!player.platformBan.banned && player.platformBan.reason === null) return problem(404, 'not_found', 'No platform ban on record for this player.');
    const platformBan: PlatformBan = { playerId: player.id, banned: false, reason: null, expiresAt: null };
    state.players.set(player.id, { ...player, banned: false, platformBan });
    return json(200, platformBan);
  }

  // -- the audit log ---------------------------------------------------------------------------

  function filterOf(source: Readonly<Record<string, unknown>>): { filter: Record<string, string> } | { refused: FakeResponse } {
    const filter: Record<string, string> = {};
    for (const key of FILTER_KEYS) {
      const value = source[key];
      if (typeof value === 'string' && value !== '') filter[key] = value;
    }
    if (filter['action'] !== undefined && !ACTION_FILTER.test(filter['action'])) {
      return { refused: invalid('action', 'Expected an action such as sessions.comp, or a prefix such as sessions.*') };
    }
    if (filter['requestId'] !== undefined && !/^req_[0-7][0-9a-z]{25}$/.test(filter['requestId'])) {
      return { refused: invalid('requestId', 'Invalid request id') };
    }
    return { filter };
  }

  function matching(filter: Readonly<Record<string, string>>): AuditEntry[] {
    return state.audit.filter((entry) => {
      const action = filter['action'];
      if (action !== undefined) {
        if (action.endsWith('.*') ? !entry.action.startsWith(action.slice(0, -1)) : entry.action !== action) return false;
      }
      if (filter['organizationId'] !== undefined && entry.organizationId !== filter['organizationId']) return false;
      if (filter['venueId'] !== undefined && entry.venueId !== filter['venueId']) return false;
      if (filter['actorId'] !== undefined && entry.actor.id !== filter['actorId']) return false;
      if (filter['actorKind'] !== undefined && entry.actor.kind !== filter['actorKind']) return false;
      if (filter['targetType'] !== undefined && entry.targetType !== filter['targetType']) return false;
      if (filter['targetId'] !== undefined && entry.targetId !== filter['targetId']) return false;
      if (filter['onBehalfOf'] !== undefined && entry.onBehalfOf?.id !== filter['onBehalfOf']) return false;
      if (filter['requestId'] !== undefined && entry.requestId !== filter['requestId']) return false;
      if (filter['from'] !== undefined && Date.parse(entry.occurredAt) < Date.parse(filter['from'])) return false;
      if (filter['to'] !== undefined && Date.parse(entry.occurredAt) >= Date.parse(filter['to'])) return false;
      return true;
    });
  }

  /** What the server writes for a read: a platform-level row, and one in every organisation shown. */
  function record(action: string, reason: string, actorId: string, filter: Readonly<Record<string, string>>, shown: readonly AuditEntry[]): void {
    state.recorded.push({ organizationId: null, action, reason, actorId, filter });
    const organizations = new Set(shown.map((entry) => entry.organizationId).filter((id): id is string => id !== null));
    for (const organizationId of organizations) state.recorded.push({ organizationId, action, reason, actorId, filter });
  }

  function reasonOf(value: unknown): string | undefined {
    const reason = typeof value === 'string' ? value.trim() : '';
    return reason.length < 3 || reason.length > 500 ? undefined : reason;
  }

  function list(searchParams: URLSearchParams, actorId: string): FakeResponse {
    const reason = reasonOf(searchParams.get('reason'));
    if (reason === undefined) return invalid('reason', 'Too small: expected at least 3 characters.');
    const checked = filterOf(Object.fromEntries(searchParams));
    if ('refused' in checked) return checked.refused;
    const { filter } = checked;
    const rows = matching(filter);
    const answer = page(rows, searchParams);
    const shown = (JSON.parse(answer.body) as { data: AuditEntry[] }).data;
    record('audit.log.read', reason, actorId, filter, shown);
    return answer;
  }

  function exported(input: Record<string, unknown>, actorId: string): FakeResponse {
    const reason = reasonOf(input['reason']);
    if (reason === undefined) return invalid('reason', 'Too small: expected at least 3 characters.');
    const checked = filterOf(input);
    if ('refused' in checked) return checked.refused;
    const { filter } = checked;
    const rows = matching(filter);
    if (rows.length > AUDIT_EXPORT_MAX_ROWS) {
      return problem(422, 'unprocessable', 'Too many rows', {
        detail: `The filter matches ${rows.length} rows; an export carries at most ${AUDIT_EXPORT_MAX_ROWS}. Narrow it.`,
      });
    }
    record('audit.log.export', reason, actorId, filter, rows);
    return csvResponse(rows, filter['organizationId'] ?? 'platform');
  }

  /** The filtered rows as a CSV download, every spreadsheet-formula cell neutralised (spec/07 §11.2). */
  function csvResponse(rows: readonly AuditEntry[], nameKey: string): FakeResponse {
    const cell = (value: unknown) => {
      const text = value === null || value === undefined ? '' : typeof value === 'string' ? value : JSON.stringify(value);
      return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
    };
    const lines = rows.map((entry) =>
      [
        entry.id,
        entry.occurredAt,
        entry.recordedAt,
        entry.actor.kind,
        entry.actor.id,
        entry.actor.label,
        entry.actorIp,
        entry.onBehalfOf ?? null,
        entry.organizationId,
        entry.venueId,
        entry.action,
        entry.targetType,
        entry.targetId,
        entry.before,
        entry.after,
        entry.reason,
        entry.requestId,
      ]
        .map(cell)
        .join(','),
    );
    return {
      status: 200,
      headers: {
        'content-type': 'text/csv; charset=utf-8',
        'content-disposition': `attachment; filename="audit-${nameKey}-2026-09-22.csv"`,
        'x-request-id': requestId,
      },
      body: [AUDIT_EXPORT_COLUMNS.join(','), ...lines].join('\r\n') + '\r\n',
    };
  }

  // -- an organisation's own log (spec/13 §15, F-ORG-14) ---------------------------------------
  // The same rows and matching, but one organisation's alone (the path scopes it) and with no reason:
  // an owner reading their own log is not the cross-organisation read a reason is asked of (spec/07 §11).

  function orgList(organizationId: string, searchParams: URLSearchParams): FakeResponse {
    const checked = filterOf(Object.fromEntries(searchParams));
    if ('refused' in checked) return checked.refused;
    return page(matching({ ...checked.filter, organizationId }), searchParams);
  }

  function orgExport(organizationId: string, input: Record<string, unknown>): FakeResponse {
    const checked = filterOf(input);
    if ('refused' in checked) return checked.refused;
    const rows = matching({ ...checked.filter, organizationId });
    if (rows.length > AUDIT_EXPORT_MAX_ROWS) {
      return problem(422, 'unprocessable', 'Too many rows', {
        detail: `The filter matches ${rows.length} rows; an export carries at most ${AUDIT_EXPORT_MAX_ROWS}. Narrow it.`,
      });
    }
    return csvResponse(rows, organizationId);
  }

  /** `GET/POST /v1/organizations/{o}/audit[/export]`, each on `audit:read`; `undefined` for other paths. */
  function handleOrganization(
    organizationId: string,
    method: string,
    pathname: string,
    searchParams: URLSearchParams,
    input: Record<string, unknown>,
    permissions: ReadonlySet<string>,
  ): FakeResponse | undefined {
    const auditPath = `/v1/organizations/${organizationId}/audit`;
    const refused = permissions.has('audit:read') ? undefined : problem(403, 'forbidden', 'Not allowed');
    if (pathname === auditPath && method === 'GET') return refused ?? orgList(organizationId, searchParams);
    if (pathname === `${auditPath}/export` && method === 'POST') return refused ?? orgExport(organizationId, input);
    return undefined;
  }

  // -- the routes ------------------------------------------------------------------------------

  const PLAYER = /^\/v1\/hq\/players\/([^/]+)(\/ban|\/unban)?$/;

  /** An answer for one of these routes, or `undefined` for a path they do not own. */
  function handle(
    method: string,
    pathname: string,
    searchParams: URLSearchParams,
    input: Record<string, unknown>,
    permissions: ReadonlySet<string>,
    actorId: string,
  ): FakeResponse | undefined {
    const refused = (permission: string) => (permissions.has(permission) ? undefined : problem(403, 'forbidden', 'Not allowed'));

    if (pathname === '/v1/hq/players' && method === 'GET') return refused('hq:players_read') ?? search(searchParams);
    const player = PLAYER.exec(pathname);
    if (player !== null) {
      const [, playerId = '', action] = player;
      if (action === undefined && method === 'GET') {
        const denied = refused('hq:players_read');
        if (denied !== undefined) return denied;
        if (!/^ply_[0-7][0-9a-z]{25}$/.test(playerId)) return invalid('playerId', 'Invalid player id');
        const found = state.players.get(playerId);
        return found === undefined ? problem(404, 'not_found', 'No such player.') : json(200, found);
      }
      if (action !== undefined && method === 'POST') {
        const denied = refused('hq:players');
        if (denied !== undefined) return denied;
        const found = state.players.get(playerId);
        if (found === undefined) return problem(404, 'not_found', 'No such player.');
        return action === '/ban' ? ban(found, input) : unban(found, input);
      }
      return problem(405, 'method_not_allowed', 'Method not allowed');
    }
    if (pathname === '/v1/hq/audit' && method === 'GET') return refused('audit:read') ?? list(searchParams, actorId);
    if (pathname === '/v1/hq/audit/export' && method === 'POST') return refused('audit:read') ?? exported(input, actorId);
    return undefined;
  }

  /** Fill the log past what one export may carry: `n` more rows, in a third organisation by default. */
  function addAuditRows(n: number, organizationId = 'org_01k5znw4hmfay8vvr14d2pf2dy'): void {
    for (let index = 0; index < n; index += 1) {
      state.audit.push(
        row({
          id: `aud_01k5znw4hmfay8vvr14d${String(index).padStart(6, '0')}`,
          occurredAt: '2026-09-01T00:00:00.000Z',
          actor: { kind: 'device', id: 'dev_01k5znw4hmfay8vvr14d2pf2d1', label: null },
          organizationId,
          venueId: null,
          action: 'sessions.start',
          targetType: 'session',
          targetId: null,
          before: null,
          after: null,
          reason: null,
          requestId: null,
        }),
      );
    }
  }

  /** Add one row to the log (its order is preserved), for a test that needs a particular shape. */
  function pushAuditRow(entry: Omit<AuditEntry, 'recordedAt' | 'actorIp'> & { recordedAt?: string }): void {
    state.audit.push(row(entry));
  }

  return { state, handle, handleOrganization, addAuditRows, pushAuditRow };
}
